import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdAdd, MdEdit, MdDelete, MdSave, MdClose, MdAttachFile, MdDownload, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { hfService } from '../../services/hfService';
import { useAuth } from '../../context/AuthContext';

const labelCls = 'text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block';
const inputCls = 'w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-md px-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none';

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString() : '';
const fmtBytes = (n) => n == null ? '' : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

const blankForm = () => ({ date: todayIso(), title: '', attendees: '', body: '', tags: [] });

const FundNotesSection = ({ fundId }) => {
  const { user } = useAuth();
  const canEdit = !!user?.is_admin || !!user?.profile?.is_hf_admin;
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState(blankForm());
  const [newTagInput, setNewTagInput] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    if (!fundId) return;
    setLoading(true);
    try {
      const res = await hfService.listFundNotes(fundId);
      setNotes(res.data.results || res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [fundId]);

  useEffect(() => { load(); }, [load]);

  const submitNew = async () => {
    if (!newForm.title.trim() || !newForm.date) {
      setError('Date and title are required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await hfService.createFundNote(fundId, newForm);
      setNewForm(blankForm());
      setNewTagInput('');
      setCreating(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    setError(null);
    try {
      await hfService.updateFundNote(editingId, editForm);
      setEditingId(null);
      setEditForm(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (noteId) => {
    if (!window.confirm('Delete this note? This also removes its attachments.')) return;
    setBusy(true);
    try {
      await hfService.deleteFundNote(noteId);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const delAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await hfService.deleteNoteAttachment(attachmentId);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const uploadAttachment = async (noteId, file) => {
    try {
      await hfService.uploadNoteAttachment(noteId, file);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  if (!fundId) return null;

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
          Meeting Notes <span className="text-sm font-normal text-gray-500 ml-1">({notes.length})</span>
        </h3>
        {!creating && canEdit && (
          <button
            onClick={() => { setCreating(true); setNewForm(blankForm()); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-brand-gold rounded-md hover:opacity-90"
          >
            <MdAdd size={14} /> Add Note
          </button>
        )}
      </div>

      {error && <div className="mb-3 text-xs text-red-500">{error}</div>}

      {creating && (
        <div className="mb-4 p-3 border border-brand-gold/40 rounded-lg bg-brand-gold/5">
          <NoteForm
            form={newForm}
            onChange={setNewForm}
            tagInput={newTagInput}
            onTagInput={setNewTagInput}
          />
          <div className="flex gap-2 justify-end mt-3">
            <button onClick={() => setCreating(false)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md">
              <MdClose size={14} /> Cancel
            </button>
            <button onClick={submitNew} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-brand-gold rounded-md hover:opacity-90 disabled:opacity-50">
              <MdSave size={14} /> {busy ? 'Saving…' : 'Save Note'}
            </button>
          </div>
        </div>
      )}

      {loading && notes.length === 0 && <div className="text-sm text-gray-500 py-4">Loading notes…</div>}

      {!loading && notes.length === 0 && !creating && (
        <div className="text-sm text-gray-400 py-6 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          No meeting notes yet for this fund.
        </div>
      )}

      <div className="space-y-2">
        {notes.map((note) => {
          const isEditing = editingId === note.id;
          const isOpen = expandedIds.has(note.id);
          if (isEditing) {
            return (
              <div key={note.id} className="border border-gray-200 dark:border-gray-800 rounded-lg p-3 bg-gray-50/40 dark:bg-gray-800/20">
                <NoteForm form={editForm} onChange={setEditForm} tagInput="" onTagInput={() => {}} />
                <div className="flex gap-2 justify-end mt-3">
                  <button onClick={() => { setEditingId(null); setEditForm(null); }} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-md">
                    <MdClose size={14} /> Cancel
                  </button>
                  <button onClick={saveEdit} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-brand-gold rounded-md hover:opacity-90 disabled:opacity-50">
                    <MdSave size={14} /> Save
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div key={note.id} className="border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50/40 dark:bg-gray-800/20 overflow-hidden">
              {/* Collapsed bar — always visible */}
              <button
                type="button"
                onClick={() => toggleExpanded(note.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-100/60 dark:hover:bg-gray-800/40 transition-colors"
                aria-expanded={isOpen}
              >
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums shrink-0 w-[88px]">
                  {fmtDate(note.date)}
                </span>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate min-w-0 flex-1">
                  {note.title}
                </span>
                {note.attendees && (
                  <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 truncate min-w-0 max-w-[40%]">
                    {note.attendees}
                  </span>
                )}
                {(isOpen ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />)}
              </button>

              {/* Expanded body */}
              {isOpen && (
                <div className="px-3 pb-3 border-t border-gray-200 dark:border-gray-800 pt-3">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      {note.attendees && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 sm:hidden">
                          <span className="font-medium">Attendees:</span> {note.attendees}
                        </div>
                      )}
                      {note.tags?.length > 0 && (
                        <div className="flex gap-1 flex-wrap mb-1">
                          {note.tags.map((t, i) => (
                            <span key={i} className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-brand-gold/10 text-brand-gold">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => { setEditingId(note.id); setEditForm({ date: note.date, title: note.title, attendees: note.attendees || '', body: note.body || '', tags: note.tags || [] }); }}
                          className="p-1 text-gray-500 hover:text-brand-gold"
                          aria-label="Edit note"
                        ><MdEdit size={16} /></button>
                        <button onClick={() => del(note.id)} className="p-1 text-gray-500 hover:text-red-500" aria-label="Delete note"><MdDelete size={16} /></button>
                      </div>
                    )}
                  </div>
                  {note.body && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed mb-2">{note.body}</p>
                  )}

                  <AttachmentsBlock
                    note={note}
                    canEdit={canEdit}
                    onUpload={(file) => uploadAttachment(note.id, file)}
                    onDelete={delAttachment}
                  />

                  <div className="mt-2 text-[11px] text-gray-400">
                    {note.author_name ? `by ${note.author_name} · ` : ''}{fmtDateTime(note.created_at)}
                    {note.updated_at !== note.created_at && <span> · edited {fmtDateTime(note.updated_at)}</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const NoteForm = ({ form, onChange, tagInput, onTagInput }) => {
  const addTag = () => {
    const t = tagInput.trim();
    if (!t || form.tags.includes(t)) return;
    onChange({ ...form, tags: [...form.tags, t] });
    onTagInput('');
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Date</label>
          <input type="date" value={form.date} onChange={(e) => onChange({ ...form, date: e.target.value })} className={inputCls} />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Title</label>
          <input type="text" value={form.title} onChange={(e) => onChange({ ...form, title: e.target.value })} placeholder="e.g. Q1 update with PM" className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Attendees</label>
        <input type="text" value={form.attendees} onChange={(e) => onChange({ ...form, attendees: e.target.value })} placeholder="John Smith, Jane Doe" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Notes</label>
        <textarea rows={6} value={form.body} onChange={(e) => onChange({ ...form, body: e.target.value })} placeholder="Discussion points, key takeaways, follow-ups…" className={`${inputCls} resize-y`} />
      </div>
      <div>
        <label className={labelCls}>Tags</label>
        <div className="flex gap-2 flex-wrap mb-2">
          {form.tags.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-brand-gold/10 text-brand-gold">
              {t}
              <button onClick={() => onChange({ ...form, tags: form.tags.filter((_, j) => j !== i) })} className="hover:text-red-500"><MdClose size={12} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="text" value={tagInput} onChange={(e) => onTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Add tag (e.g. update, DD)" className={`${inputCls} flex-1`} />
          <button type="button" onClick={addTag} className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-md">Add</button>
        </div>
      </div>
    </div>
  );
};

const AttachmentsBlock = ({ note, canEdit, onUpload, onDelete }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const triggerUpload = () => fileRef.current?.click();
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="mt-2">
      {note.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {note.attachments.map((a) => (
            <div key={a.id} className="flex items-center gap-2 px-2 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md text-xs">
              <a href={a.file_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-gray-700 dark:text-gray-300 hover:text-brand-gold truncate max-w-[220px]">
                <MdDownload size={12} />
                <span className="truncate">{a.filename}</span>
                <span className="text-gray-400">{fmtBytes(a.size_bytes)}</span>
              </a>
              {canEdit && (
                <button onClick={() => onDelete(a.id)} className="text-gray-400 hover:text-red-500" aria-label="Delete attachment"><MdClose size={12} /></button>
              )}
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <>
          <input type="file" ref={fileRef} onChange={handleFile} className="hidden" />
          <button onClick={triggerUpload} disabled={uploading} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-brand-gold">
            <MdAttachFile size={14} /> {uploading ? 'Uploading…' : 'Attach file'}
          </button>
        </>
      )}
    </div>
  );
};

export default FundNotesSection;
